import api from '@/api/api'
import type { DhChatScope } from '@/api/messenger/types'

/**
 * Сетевой режим Мультичата: на main сети видны чаты всех филиалов.
 *
 * Включается в админке. Фронт о филиалах почти ничего не знает: threadId
 * на main приходит составным, а владельца чата BFF находит сам. Скоуп нужен
 * только чтобы принимать realtime-события филиалов (они приходят с
 * projectId филиала) и подписывать треды названием филиала.
 */
const CACHE_TTL_MS = 60_000
const cache = new Map<string, { scope: DhChatScope; expiresAt: number }>()
const pending = new Map<string, Promise<DhChatScope | null>>()

export function getCachedNetworkChatScope(projectId?: string | null): DhChatScope | null {
    if (!projectId) return null
    return cache.get(projectId)?.scope ?? null
}

export async function loadNetworkChatScope(projectId?: string | null): Promise<DhChatScope | null> {
    if (!projectId) return null
    const cached = cache.get(projectId)
    if (cached && cached.expiresAt > Date.now()) return cached.scope
    const inFlight = pending.get(projectId)
    if (inFlight) return inFlight

    const request = (async () => {
        const { data, error } = await api.messenger.chat.getChatScope({ projectId })
        if (error || !data) return cached?.scope ?? null
        cache.set(projectId, { scope: data, expiresAt: Date.now() + CACHE_TTL_MS })
        return data
    })()
    pending.set(projectId, request)
    try {
        return await request
    } finally {
        pending.delete(projectId)
    }
}

/** Событие проекта eventProjectId относится к чату, открытому на viewProjectId. */
export function isInNetworkChatScope(
    scope: DhChatScope | null | undefined,
    viewProjectId: string,
    eventProjectId: string
): boolean {
    if (eventProjectId === viewProjectId) return true
    return Boolean(scope?.network && scope.projectIds.includes(eventProjectId))
}

/** Название филиала-владельца треда; null вне сетевого режима. */
export function networkChatProjectName(
    scope: DhChatScope | null | undefined,
    projectId?: string | null
): string | null {
    if (!scope?.network || !projectId) return null
    return scope.projects.find((project) => project.id === projectId)?.name || null
}

type NetworkChannelLike = {
    label?: string | null
    connectorName?: string | null
    connectorId?: string | null
    projectId?: string | null
}

/** Филиал-владелец канала: projectId от BFF или префикс connectorId («<проект>_…»). */
export function networkChannelProjectId(channel: NetworkChannelLike | null | undefined): string | null {
    return channel?.projectId || channel?.connectorId?.split('_')[0] || null
}

export type NetworkChannelBranch<T> = { id: string; name: string; channels: T[] }

/**
 * Каналы main, разложенные по филиалам в порядке скоупа — для выбора
 * «сначала филиал, потом канал». Вне сетевого режима — пусто.
 */
export function groupChannelsByBranch<T extends NetworkChannelLike>(
    scope: DhChatScope | null | undefined,
    channels: T[]
): NetworkChannelBranch<T>[] {
    if (!scope?.network) return []
    const branches: NetworkChannelBranch<T>[] = scope.projects.map((project) => ({
        id: project.id,
        name: project.name || project.id,
        channels: [],
    }))
    for (const channel of channels) {
        const branch = branches.find((item) => item.id === networkChannelProjectId(channel))
        branch?.channels.push(channel)
    }
    return branches.filter((branch) => branch.channels.length > 0)
}

/** Подпись канала внутри уже выбранного филиала — без «Филиал · ». */
export function channelTitleWithinBranch(title: string, branchName?: string | null): string {
    const prefix = branchName ? `${branchName} · ` : ''
    return prefix && title.startsWith(prefix) ? title.slice(prefix.length) : title
}

/**
 * Подпись канала на main. Коннекторов филиалов в проекте main нет, поэтому
 * обычная подпись выходит голым «Telegram» — и одинаковые каналы разных
 * филиалов не различить. BFF подписывает каналы сам («Филиал · Телеграм»);
 * если подписи нет, ставим название филиала перед обычной.
 */
export function networkChannelTitle(
    scope: DhChatScope | null | undefined,
    channel: NetworkChannelLike | null | undefined,
    fallbackTitle: string
): string {
    if (!scope?.network || !channel) return fallbackTitle
    const ownTitle = channel.label || channel.connectorName
    if (ownTitle) return ownTitle
    const branchName = networkChatProjectName(scope, networkChannelProjectId(channel))
    return branchName ? `${branchName} · ${fallbackTitle}` : fallbackTitle
}
